import { telHref, type ClientListItem } from '@kominiarz/shared';
import { ChevronRight, MapPin, Phone, Plus, Search, Users } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Alert } from '../../components/Alert';
import { Button, ButtonLink } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Spinner } from '../../components/Loading';
import { PageBody } from '../../components/PageBody';
import { PageHeader } from '../../components/PageHeader';
import { errorMessage } from '../../lib/api';
import { useClientSearch, useDebounced } from '../../lib/clientsApi';
import styles from './clients.module.css';

const PAGE = 40;

export function ClientsPage() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get('q') ?? '');
  const [limit, setLimit] = useState(PAGE);
  const debounced = useDebounced(query.trim());
  const results = useClientSearch(debounced, { limit });

  function onQuery(value: string) {
    setQuery(value);
    setLimit(PAGE);
    setParams(value.trim() ? { q: value.trim() } : {}, { replace: true });
  }

  const clients = results.data?.clients ?? [];
  const newHref = `/klienci/nowy${debounced ? `?nazwa=${encodeURIComponent(debounced)}` : ''}`;

  return (
    <>
      <PageHeader
        title="Klienci"
        actions={
          <ButtonLink to="/klienci/nowy" variant="primary" icon={<Plus aria-hidden="true" />}>
            Dodaj
          </ButtonLink>
        }
      />
      <PageBody>
        <label className={styles.search}>
          <span className="visually-hidden">Szukaj klienta</span>
          <Search aria-hidden="true" className={styles.searchIcon} />
          <input
            type="search"
            enterKeyHint="search"
            autoComplete="off"
            spellCheck={false}
            placeholder="Nazwisko, telefon albo ulica"
            value={query}
            onChange={(e) => onQuery(e.target.value)}
          />
        </label>

        {results.isError && <Alert kind="error">{errorMessage(results.error)}</Alert>}
        {results.isPending && <Spinner />}
        {results.data && clients.length === 0 && (
          debounced ? (
            <EmptyState
              icon={Search}
              title={`Nie znaleziono: „${debounced}”`}
              action={
                <ButtonLink to={newHref} variant="primary" icon={<Plus aria-hidden="true" />}>
                  Dodaj nowego klienta
                </ButtonLink>
              }
            >
              Sprawdź pisownię albo szukaj po numerze telefonu lub ulicy.
            </EmptyState>
          ) : (
            <EmptyState
              icon={Users}
              title="Nie ma jeszcze klientów"
              action={
                <ButtonLink to="/klienci/nowy" variant="primary" icon={<Plus aria-hidden="true" />}>
                  Dodaj klienta
                </ButtonLink>
              }
            >
              Klienta można też dodać od razu przy nowej wizycie.
            </EmptyState>
          )
        )}
        {clients.length > 0 && (
          <ul className={styles.list} aria-busy={results.isFetching}>
            {clients.map((c) => (
              <ClientRow key={c.id} client={c} />
            ))}
          </ul>
        )}
        {results.data?.hasMore && (
          <Button variant="secondary" onClick={() => setLimit((l) => l + PAGE)} disabled={results.isFetching}>
            Pokaż więcej
          </Button>
        )}
      </PageBody>
    </>
  );
}

function ClientRow({ client: c }: { client: ClientListItem }) {
  const phone = c.phones[0]?.phone;
  return (
    <li className={styles.row}>
      <Link to={`/klienci/${c.id}`} className={styles.rowLink}>
        <span className={styles.rowName}>{c.name ?? c.phones[0]?.phone}</span>
        {phone && (
          <span className={styles.rowMeta}>
            <Phone aria-hidden="true" />
            {c.phones.map((p) => p.phone).join(', ')}
          </span>
        )}
        {c.objects[0] && (
          <span className={styles.rowMeta}>
            <MapPin aria-hidden="true" />
            {c.objects[0].address}
            {c.objects.length > 1 ? ` i jeszcze ${c.objects.length - 1}` : ''}
          </span>
        )}
      </Link>
      {phone ? (
        <a className={styles.call} href={telHref(phone)}>
          <Phone aria-hidden="true" />
          <span>Zadzwoń</span>
        </a>
      ) : (
        <ChevronRight aria-hidden="true" className={styles.chevron} />
      )}
    </li>
  );
}
